import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
    isPushSupported,
    isIos,
    isStandalone,
    getCurrentPermission,
    subscribeToPush,
} from "../lib/push";

const DISMISS_KEY = "kw_push_dismissed_at";
const DISMISS_HOURS = 72; // Snooze 3 days after a dismiss

function recentlyDismissed() {
    try {
        const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        if (!at) return false;
        return Date.now() - at < DISMISS_HOURS * 3600 * 1000;
    } catch {
        return false;
    }
}

export default function PushOptInBanner() {
    const [show, setShow] = useState(false);
    const [iosHint, setIosHint] = useState(false);
    const [busy, setBusy] = useState(false);
    const [postInstall, setPostInstall] = useState(false);

    const evaluate = async (force = false) => {
        if (!isPushSupported()) {
            if (isIos() && !isStandalone() && !recentlyDismissed()) {
                setIosHint(true);
            }
            return;
        }
        if (isIos() && !isStandalone()) {
            if (!recentlyDismissed()) setIosHint(true);
            return;
        }
        const perm = await getCurrentPermission();
        if (perm === "default" && (force || !recentlyDismissed())) {
            setShow(true);
        }
    };

    useEffect(() => {
        // First-load decision
        try {
            if (sessionStorage.getItem("kw_just_installed") === "1") {
                sessionStorage.removeItem("kw_just_installed");
                setPostInstall(true);
                evaluate(true);
            } else {
                evaluate(false);
            }
        } catch {
            evaluate(false);
        }
        const onInstalled = () => {
            setPostInstall(true);
            evaluate(true);
        };
        window.addEventListener("kw-just-installed", onInstalled);
        return () =>
            window.removeEventListener("kw-just-installed", onInstalled);
    }, []);

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
        setShow(false);
        setIosHint(false);
    };

    const enable = async () => {
        setBusy(true);
        try {
            await subscribeToPush();
            toast.success("Push notifications enabled");
            setShow(false);
        } catch (e) {
            if (e?.message === "denied") {
                toast.error("Notifications were blocked");
                dismiss();
            } else if (e?.message === "ios-needs-install") {
                setShow(false);
                setIosHint(true);
            } else if (e?.message === "unsupported") {
                toast.error("This browser doesn't support push");
            } else {
                const detail =
                    e?.response?.data?.detail ||
                    e?.message ||
                    "unknown error";
                toast.error(`Could not enable notifications: ${detail}`);
                // eslint-disable-next-line no-console
                console.error("Push enable failed", e);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {iosHint && (
                <motion.div
                    data-testid="push-ios-hint"
                    className="fixed left-4 right-4 z-[115] mx-auto max-w-sm bg-[#121212] border border-white/10 rounded-2xl p-4 shadow-2xl"
                    style={{ top: "max(1rem, calc(env(safe-area-inset-top) + 0.5rem))" }}
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                >
                    <div className="flex items-start gap-3">
                        <Smartphone size={20} className="kw-orange flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                                Push on iPhone
                            </div>
                            <div className="text-[12px] text-white/60 leading-relaxed mt-1">
                                Apple allows push only after you add this shop
                                to your Home Screen.
                                <br />
                                Tap the{" "}
                                <span className="kw-orange font-bold">
                                    Share
                                </span>{" "}
                                icon in Safari ▸ choose{" "}
                                <span className="kw-orange font-bold">
                                    "Add to Home Screen"
                                </span>{" "}
                                — then open KYLIE WEST from your home screen
                                and you'll see the enable button.
                            </div>
                        </div>
                        <button
                            data-testid="push-ios-dismiss"
                            onClick={dismiss}
                            className="p-1 rounded-full text-white/50 hover:text-white"
                            aria-label="Dismiss"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </motion.div>
            )}

            {show && (
                <motion.div
                    data-testid="push-optin-banner"
                    className="fixed left-4 right-4 z-[115] mx-auto max-w-sm bg-[#121212] border border-[#FF5A00]/40 rounded-2xl p-4 shadow-2xl kw-glow"
                    style={{ top: "max(1rem, calc(env(safe-area-inset-top) + 0.5rem))" }}
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                >
                    <div className="flex items-start gap-3">
                        <Bell size={20} className="kw-orange flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                                {postInstall
                                    ? "App installed — last step!"
                                    : "Be first on new drops"}
                            </div>
                            <div className="text-[12px] text-white/60 leading-relaxed mt-1">
                                {postInstall
                                    ? "Enable notifications so every new KYLIE WEST drop pops up the second it goes live."
                                    : "Enable notifications to get every new KYLIE WEST drop straight to your phone."}
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    data-testid="push-enable-btn"
                                    onClick={enable}
                                    disabled={busy}
                                    className="px-4 py-2 kw-orange-bg text-black font-bold rounded-lg uppercase tracking-wider text-[11px] disabled:opacity-60"
                                >
                                    {busy
                                        ? "Enabling…"
                                        : postInstall
                                        ? "Yes, enable"
                                        : "Enable"}
                                </button>
                                <button
                                    data-testid="push-dismiss-btn"
                                    onClick={dismiss}
                                    className="px-3 py-2 rounded-lg border border-white/15 text-white/60 hover:text-white text-[11px] uppercase tracking-wider"
                                >
                                    Not now
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={dismiss}
                            className="p-1 rounded-full text-white/50 hover:text-white"
                            aria-label="Close"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
